import { styled, alpha } from '@mui/material/styles';
import { Box, Typography, TextField, Table, TableHead, TableBody, TableCell } from '@mui/material';
import { primaryColor } from '../../utils/theme';

export const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(2),
}));

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 600,
  color: theme.palette.custom.sidebarText,
}));

export const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  width: '100%',
  flexWrap: 'wrap',
}));

export const SearchInput = styled(TextField)(({ theme }) => ({
  flex: '1 1 300px',
  maxWidth: 300,
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.white.main,
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&.Mui-focused fieldset': {
      borderColor: primaryColor,
      borderWidth: '1px',
    },
    '& .MuiInputAdornment-root': {
      color: theme.palette.grey[500],
    },
  },
}));

export const StyledTableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
  overflowX: 'auto',
  boxShadow: 'none',
}));

export const StyledTable = styled(Table)({
  minWidth: 800,
  borderCollapse: 'separate',
  borderSpacing: 0,
});

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,

  '& th': {
    backgroundColor: 'inherit',
    color: theme.palette.custom.sidebarText,
    fontWeight: 600,
    fontSize: '0.85rem',
    borderBottom: 'none',
    padding: theme.spacing(1.5, 2),
  },
}));

export const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& tr:nth-of-type(odd)': {
    backgroundColor: theme.palette.white.main,
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: theme.palette.primary.light,
  },
  '& tr:last-child td': {
    borderBottom: 0,
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.85rem',
  color: theme.palette.custom.sidebarText,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  padding: theme.spacing(1.5, 2),
}));

export const ListContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.palette.custom.borderRadius,
}));

export const PaginationFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
  color: theme.palette.grey[600],
  fontSize: '0.85rem',
  paddingBottom: 0,
}));

interface StatusTagProps {
  status: string;
}

export const StatusTag = styled(Box)<StatusTagProps>(({ theme, status }) => {
  let bgColor: string;
  let color: string;

  switch (status.toLowerCase()) {
    case 'requested':
    case 'pending':
      bgColor = alpha(theme.palette.warning.light, 0.5);
      color = theme.palette.warning.dark;
      break;
    case 'approved':
    case 'active':
      bgColor = alpha(primaryColor, 0.15);
      color = primaryColor;
      break;
    case 'rejected':
    case 'cancelled':
      bgColor = alpha(theme.palette.error.main, 0.15);
      color = theme.palette.error.main;
      break;
    default:
      bgColor = theme.palette.grey[100];
      color = theme.palette.grey[600];
  }

  return {
    backgroundColor: bgColor,
    color: color,
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '4px',
    width: 'fit-content',
  };
});
